import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/adminApi";
import { fmtINR, fmtDate, fmtDateTime } from "../lib/format";
import { Plus, Trash2, Pencil, IndianRupee, Loader2, X, Check } from "lucide-react";

export default function BillingTab({ tenantId }) {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ lifetime: 0, outstanding: 0, notes: "", lastPaid: null });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEditBilling, setShowEditBilling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [payRes, tenantRes] = await Promise.all([
        api.get(`/tenants/${tenantId}/payments`),
        api.get(`/tenants/${tenantId}`),
      ]);
      const items = payRes.data.items || [];
      setPayments(items);
      setSummary({
        lifetime: payRes.data.lifetime_total_inr || 0,
        outstanding: tenantRes.data.outstanding_balance_inr || 0,
        notes: tenantRes.data.billing_notes || "",
        lastPaid: items.length > 0 ? items[0].paid_at : null,
      });
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const deletePay = async (payId) => {
    if (!window.confirm("Delete this payment?")) return;
    try {
      await api.delete(`/tenants/${tenantId}/payments/${payId}`);
      load();
    } catch (err) { alert(err.response?.data?.detail || "Delete failed"); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#7C5CFF]" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="billing-tab">
      {/* Summary row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryCard label="Lifetime Payments" value={fmtINR(summary.lifetime)} color="#10B981" testId="billing-lifetime" />
        <SummaryCard label="Outstanding Balance" value={fmtINR(summary.outstanding)} color={summary.outstanding > 0 ? "#EF4444" : "#10B981"} testId="billing-outstanding" />
        <SummaryCard label="Last Payment" value={summary.lastPaid ? fmtDate(summary.lastPaid) : "—"} color="#5B617A" testId="billing-last-paid" />
      </div>

      {/* Payments table */}
      <div className="glass-card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#7C5CFF]/[0.08]">
          <p className="text-sm font-bold text-[#0B0F1A]">Payments</p>
          <div className="flex gap-2">
            <button data-testid="edit-billing-btn" onClick={() => setShowEditBilling(true)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl border border-[#7C5CFF]/[0.12] text-xs font-semibold text-[#0B0F1A] hover:bg-[#7C5CFF]/[0.06]">
              <Pencil className="w-3 h-3" />Edit Billing
            </button>
            <button data-testid="add-payment-btn" onClick={() => setShowAdd(true)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold text-white btn-primary-glass">
              <Plus className="w-3 h-3" />Add Payment
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead>
              <tr className="bg-white/40 border-b border-[#7C5CFF]/[0.08]">
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Date</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Amount</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Method</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Reference</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Notes</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">By</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-[#5B617A]">No payments recorded yet.</td></tr>
              ) : payments.map((p) => (
                <tr key={p.id} className="border-b border-[#7C5CFF]/[0.04] glass-row-hover" data-testid={`payment-row-${p.id}`}>
                  <td className="px-3 py-2.5 text-xs text-[#0B0F1A]">{fmtDate(p.paid_at)}</td>
                  <td className="px-3 py-2.5 text-sm font-semibold text-[#0B0F1A]" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINR(p.amount_inr)}</td>
                  <td className="px-3 py-2.5">
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#7C5CFF]/[0.08] text-[#5B617A]">{p.method}</span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-[#5B617A] max-w-[120px] truncate">{p.reference || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-[#5B617A] max-w-[120px] truncate">{p.notes || "—"}</td>
                  <td className="px-3 py-2.5 text-[10px] text-[#5B617A] truncate max-w-[100px]">{p.recorded_by_email || "—"}</td>
                  <td className="px-3 py-2.5">
                    <button data-testid={`delete-payment-${p.id}`} onClick={() => deletePay(p.id)}
                      className="p-1 rounded hover:bg-red-50 transition-colors">
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && <AddPaymentModal tenantId={tenantId} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />}
      {showEditBilling && <EditBillingModal tenantId={tenantId} initial={summary} onClose={() => setShowEditBilling(false)} onSaved={() => { setShowEditBilling(false); load(); }} />}
    </div>
  );
}

function SummaryCard({ label, value, color, testId }) {
  return (
    <div className="glass-card p-4" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        <IndianRupee className="w-3.5 h-3.5" style={{ color }} />
        <span className="text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-bold" style={{ color, fontVariantNumeric: "tabular-nums" }}>{value}</p>
    </div>
  );
}

function AddPaymentModal({ tenantId, onClose, onAdded }) {
  const todayIST = () => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  };
  const [amount, setAmount] = useState("");
  const [paidAt, setPaidAt] = useState(todayIST());
  const [method, setMethod] = useState("upi");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { setError("Amount must be > 0"); return; }
    if (!paidAt) { setError("Date is required"); return; }
    setSaving(true); setError("");
    try {
      await api.post(`/tenants/${tenantId}/payments`, {
        amount_inr: parseFloat(amount),
        paid_at: paidAt,
        method,
        reference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onAdded();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add payment");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-modal w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()} data-testid="add-payment-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#7C5CFF]/[0.08]">
          <p className="font-bold text-[#0B0F1A] text-sm">Add Payment</p>
          <button onClick={onClose} data-testid="add-payment-close"><X className="w-4 h-4 text-[#5B617A]" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          {error && <p className="text-red-600 text-sm bg-red-50 p-2 rounded-xl" data-testid="add-payment-error">{error}</p>}
          <div>
            <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">Amount (₹) *</label>
            <input data-testid="payment-amount" type="number" step="1" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full h-10 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm glass-input" placeholder="5000" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">Date *</label>
            <input data-testid="payment-date" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)}
              className="w-full h-10 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm glass-input" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">Method</label>
            <select data-testid="payment-method" value={method} onChange={(e) => setMethod(e.target.value)}
              className="w-full h-10 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm focus:outline-none focus:border-[#7C5CFF]">
              {["cash", "upi", "bank_transfer", "card", "other"].map((m) => (
                <option key={m} value={m}>{m.replace("_", " ")}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">Reference</label>
            <input data-testid="payment-reference" value={reference} onChange={(e) => setReference(e.target.value)}
              className="w-full h-10 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm glass-input" placeholder="UPI ref, cheque no." />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">Notes</label>
            <input data-testid="payment-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full h-10 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm glass-input" placeholder="Optional notes" />
          </div>
          <button data-testid="payment-submit" type="submit" disabled={saving}
            className="w-full h-10 rounded-xl text-white font-semibold text-sm flex items-center justify-center btn-primary-glass">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Payment"}
          </button>
        </form>
      </div>
    </div>
  );
}

function EditBillingModal({ tenantId, initial, onClose, onSaved }) {
  const [outstanding, setOutstanding] = useState(String(initial.outstanding || "0"));
  const [billingNotes, setBillingNotes] = useState(initial.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await api.patch(`/tenants/${tenantId}/billing`, {
        outstanding_balance_inr: parseFloat(outstanding),
        billing_notes: billingNotes,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-modal w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()} data-testid="edit-billing-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#7C5CFF]/[0.08]">
          <p className="font-bold text-[#0B0F1A] text-sm">Edit Billing</p>
          <button onClick={onClose} data-testid="edit-billing-close"><X className="w-4 h-4 text-[#5B617A]" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          {error && <p className="text-red-600 text-sm bg-red-50 p-2 rounded-xl">{error}</p>}
          <div>
            <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">Outstanding Balance (₹)</label>
            <input data-testid="edit-outstanding" type="number" step="1" value={outstanding} onChange={(e) => setOutstanding(e.target.value)}
              className="w-full h-10 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm glass-input" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">Billing Notes</label>
            <textarea data-testid="edit-billing-notes" value={billingNotes} onChange={(e) => setBillingNotes(e.target.value)}
              className="w-full bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl p-3 text-sm h-20 resize-none glass-input" />
          </div>
          <button data-testid="edit-billing-submit" type="submit" disabled={saving}
            className="w-full h-10 rounded-xl text-white font-semibold text-sm flex items-center justify-center btn-primary-glass">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}
